import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Table,
  Button
} from "react-bootstrap";
import { Download, ArrowDown, ArrowUp } from "lucide-react";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import TooltipOption from "../../../core/common/tooltipOption";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { _get } from "../../Utils/Helper";

const ProfitLoss = () => {
  const [filterType, setFilterType] = useState<"daily" | "monthly" | "session">(
    "daily"
  );
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  interface Transaction {
    total_income: string;
    total_expenses: string;
    type: string;
    id: string;
    date: string;
    category: string;
    description: string;
    amount: number;
    paymentMethod: string;
  }

  interface Report {
    id: string;
    date: string;
    category: string;
    description: string;
    amount: number;
    payment_method: string;
  }

  const [reports, setReports] = useState<Report[]>([]);
  const [profitLossSummary, setProfitLossSummary] = useState({
    total_income: 0,
    total_expenses: 0,
    balance: 0
  });

  const filteredTransactions = Array.isArray(profitLossSummary)
    ? profitLossSummary.filter((transaction) => {
        if (filterType === "daily" && selectedDate) {
          return transaction.date === selectedDate;
        }
        if (filterType === "monthly" && selectedMonth) {
          return transaction.date.startsWith(selectedMonth);
        }
        if (filterType === "session" && selectedSession) {
          return true;
        }
        return true;
      })
    : [];

  const totalIncome = filteredTransactions.reduce((sum, t) => {
    const amount = parseFloat(t.total_income);

    return isNaN(amount) ? sum : sum + amount;
  }, 0);

  const totalExpenses = filteredTransactions.reduce((sum, t) => {
    const amount = parseFloat(t.total_expenses);

    return isNaN(amount) ? sum : sum + amount;
  }, 0);

  const netProfit = totalIncome - totalExpenses;

  const handleDownloadExcel = () => {
    const data = reports.map((r) => ({
      Date: format(new Date(r.date), "yyyy-MM-dd"),
      Category: r.category,
      Description: r.description,
      Amount: r.amount,
      "Payment Method": r.payment_method
    }));

    const summary = [
      { Label: "Total Income", Amount: profitLossSummary.total_income },
      { Label: "Total Expenses", Amount: profitLossSummary.total_expenses },
      { Label: "Net Profit/Loss", Amount: profitLossSummary.balance }
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    const summaryWs = XLSX.utils.json_to_sheet(summary);

    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

    XLSX.writeFile(wb, "profit-loss-report.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text("Profit & Loss Report", 14, 15);

    // Add transactions table
    (doc as any).autoTable({
      head: [["Date", "Category", "Description", "Amount", "Payment Method"]],
      body: reports.map((r) => [
        format(new Date(r.date), "yyyy-MM-dd"),
        r.category,
        r.description,
        r.amount.toLocaleString(),
        r.payment_method
      ]),
      startY: 35
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;

    // Add totals with colors
    doc.setFontSize(14);

    // Total Income (Green)
    doc.setTextColor(0, 128, 0); // Green
    doc.text(
      `Total Income: ${profitLossSummary.total_income.toLocaleString()}`,
      14,
      finalY
    );

    // Total Expenses (Red)
    doc.setTextColor(255, 0, 0); // Red
    doc.text(
      `Total Expenses: ${profitLossSummary.total_expenses.toLocaleString()}`,
      14,
      finalY + 10
    );

    // Net Profit/Loss (Green or Red based on value)
    if (profitLossSummary.balance >= 0) {
      doc.setTextColor(0, 128, 0); // Green for positive balance
    } else {
      doc.setTextColor(255, 0, 0); // Red for negative balance
    }
    doc.text(
      `Net Profit/Loss: ${profitLossSummary.balance.toLocaleString()}`,
      14,
      finalY + 20
    );

    // Generate today's date for the filename
    const today = format(new Date(), "yyyy-MM-dd");

    // Save the PDF
    doc.save(`profit-loss-report-${today}.pdf`);
  };

  const routes = all_routes;

  const fetchProfitLossData = () => {
    _get(
      "profit-loss-report",
      (data: any) => {
        setProfitLossSummary({
          total_income: parseFloat(data.total_income) || 0,
          total_expenses: parseFloat(data.total_expenses) || 0,
          balance: parseFloat(data.balance) || 0
        });
      },
      (err) => console.error("Failed to fetch profit/loss data:", err)
    );
  };

  const fetchReports = () => {
    _get(
      "get-all-reports",
      (data: any[]) => setReports(data),
      (err) => console.error("Failed to fetch reports:", err)
    );
  };

  useEffect(() => {
    fetchProfitLossData();
    fetchReports();
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Profit And Loss Report</h3>
            {/* <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Report</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Profit and Loss Report
                </li>
              </ol>
            </nav> */}
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption />
          </div>
        </div>
        <Container className="py-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h2 className="mb-0">Profit & Loss Statement</h2>
              <div>
                <Button
                  variant="outline-primary"
                  className="me-2"
                  onClick={handleDownloadExcel}
                >
                  <Download size={16} className="me-2" />
                  Download Excel
                </Button>
                <Button variant="outline-primary" onClick={handleDownloadPDF}>
                  <Download size={16} className="me-2" />
                  Download PDF
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter Type</Form.Label>
                    <Form.Select
                      value={filterType}
                      onChange={(e) =>
                        setFilterType(
                          e.target.value as "daily" | "monthly" | "session"
                        )
                      }
                    >
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                      <option value="session">Session</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  {filterType === "daily" && (
                    <Form.Group>
                      <Form.Label>Select Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </Form.Group>
                  )}
                  {filterType === "monthly" && (
                    <Form.Group>
                      <Form.Label>Select Month</Form.Label>
                      <Form.Control
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      />
                    </Form.Group>
                  )}
                  {filterType === "session" && (
                    <Form.Group>
                      <Form.Label>Select Session</Form.Label>
                      <Form.Select
                        value={selectedSession}
                        onChange={(e) => setSelectedSession(e.target.value)}
                      >
                        <option value="">Select Session</option>
                        <option value="2023/2024">2023/2024</option>
                        <option value="2024/2025">2024/2025</option>
                      </Form.Select>
                    </Form.Group>
                  )}
                </Col>
              </Row>

              <Card className="mb-4">
                <Card.Body>
                  <Row className="mb-4">
                    <Col md={4}>
                      <div className="text-center">
                        <h4>Total Income</h4>
                        <h3 className="text-success">
                          {profitLossSummary.total_income.toLocaleString()}
                          <ArrowUp className="ms-2" size={24} />
                        </h3>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center">
                        <h4>Total Expenses</h4>
                        <h3 className="text-danger">
                          {profitLossSummary.total_expenses.toLocaleString()}
                          <ArrowDown className="ms-2" size={24} />
                        </h3>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center">
                        <h4>Net Profit/Loss</h4>
                        <h3
                          className={
                            profitLossSummary.balance >= 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {profitLossSummary.balance.toLocaleString()}
                          {profitLossSummary.balance >= 0 ? (
                            <ArrowUp className="ms-2" size={24} />
                          ) : (
                            <ArrowDown className="ms-2" size={24} />
                          )}
                        </h3>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row className="mt-4">
                <Col>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <span>Transaction Reports</span>
                      <div>
                        <Button
                          variant="outline-primary"
                          className="me-2"
                          onClick={handleDownloadExcel}
                        >
                          <Download size={16} className="me-2" />
                          Download Excel
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={handleDownloadPDF}
                        >
                          <Download size={16} className="me-2" />
                          Download PDF
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Category</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Payment Method</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.map((report) => (
                            <tr key={report.id}>
                              <td>{report.date}</td>
                              <td>{report.category}</td>
                              <td>{report.description}</td>
                              <td className="text-right">
                                {report.amount.toLocaleString()}
                              </td>
                              <td>{report.payment_method}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default ProfitLoss;
